import { PropsWithChildren } from 'react';
import { RouteObject } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import { RouteConfig, SyncHook } from './route-types';

const getSyncHookWrapper =
  (useSyncHook: SyncHook) =>
  ({ children }: PropsWithChildren) => {
    useSyncHook();
    return <>{children}</>;
  };

/**
 * getAppRoutesFromConfigs returns the final route list from a set of routeConfigs.
 * For some routes in our application we want to sync the route information with the state,
 * and for those routes we create a "useSyncHook" where we put that sync logic. When there is a sync hook we want to
 * wrap the element with the SyncHookWrapper which will ensure the useSyncHook is ran for those routes.
 * @param routeConfigs configurations for different sets of routes in our app.
 * @returns RouteObject[] ready for react-router
 */
export const getAppRoutesFromConfigs = (routeConfigs: RouteConfig[]) => {
  return routeConfigs.reduce((prevRoutes, routeConfig) => {
    let subRoutes: RouteObject[] = routeConfig.routes;

    // If the routeConfig has a useSyncHook attached to it then wrap the element with the SyncHookWrapper that runs the useSyncHook for that route.
    if (routeConfig.useSyncHook) {
      const SyncHookWrapper = getSyncHookWrapper(routeConfig.useSyncHook);
      subRoutes = subRoutes.reduce((prevSubRoutes, subRoute) => {
        const newSubRoute = {
          ...subRoute,
          element: <SyncHookWrapper>{subRoute.element}</SyncHookWrapper>,
        };
        return [...prevSubRoutes, newSubRoute];
      }, [] as RouteObject[]);
    }

    if (!routeConfig.publicRoute) {
      subRoutes = subRoutes.reduce((prevSubRoutes, subRoute) => {
        const newSubRoute = {
          ...subRoute,
          element: <ProtectedRoute>{subRoute.element}</ProtectedRoute>,
        };
        return [...prevSubRoutes, newSubRoute];
      }, [] as RouteObject[]);
    }
    return [...prevRoutes, ...subRoutes];
  }, [] as RouteObject[]);
};
